import { getToken } from '../lib/auth';
import { HttpError } from './client';

// Firebase Hosting BUFFERS streaming responses through its /api/** rewrite, so
// SSE delivers nothing live in production (verified: 0 events through Hosting vs
// instant through Cloud Run). The live EventSource therefore connects DIRECTLY
// to the Cloud Run service (VITE_STREAM_BASE, set at build time). CORS allows
// decks.rdx.tools and the stream is anonymous, so a cross-origin EventSource is
// fine. Everything else keeps going through Hosting. Empty in dev → same-origin.
const STREAM_BASE = (import.meta.env.VITE_STREAM_BASE as string | undefined) ?? '';

/** The synced cursor broadcast to viewers. Mirrors domain.LivePosition (Go). */
export interface LivePosition {
  slideId: string;
  buildStep: number;
  seq: number;
  updatedAt: string;
}

/** Notes-stripped viewer payload. `model` is a SlideDeck JSON with notes removed. */
export interface ViewerSession {
  id: string;
  status: 'live' | 'paused' | 'ended';
  position: LivePosition;
  languages?: string[];
  model: unknown; // parsed as SlideDeck by the caller
}

export interface CreateLiveResponse {
  sessionId: string;
  url: string;
}

async function authed<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init?.headers as Record<string, string> | undefined) ?? {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(path, { ...init, headers });
  if (!res.ok) throw new HttpError(res.status, res.statusText);
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

async function anon<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (!res.ok) throw new HttpError(res.status, res.statusText);
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

/** Per-locale inline-edit overrides the renderer consumes: slideId -> regionId -> text. */
export type DeckOverrides = Record<string, Record<string, string>>;

/** A typed event multiplexed over the live SSE stream. Exactly one payload field
 *  is present per event, selected by `type`. Mirrors domain.LiveEvent (Go). */
export interface LiveEvent {
  type: 'position' | 'presence' | 'question';
  position?: LivePosition;
  presence?: PresenceSnapshot;
  questions?: Question[];
}

/** Live audience aggregate: a count + per-locale breakdown. Mirrors domain.PresenceSnapshot. */
export interface PresenceSnapshot {
  count: number;
  byLocale: Record<string, number>;
}

/** One shared-Q&A question (broadcast shape — no voter list). Mirrors domain.Question. */
export interface Question {
  id: string;
  name: string;
  text: string;
  votes: number;
  status: 'open' | 'answered' | 'hidden';
  createdAt: string;
}

export const liveApi = {
  /** Presenter: create a session for a deck. */
  create: (deckId: string, languages: string[] = ['en']): Promise<CreateLiveResponse> =>
    authed<CreateLiveResponse>(`/api/decks/${encodeURIComponent(deckId)}/live`, {
      method: 'POST',
      body: JSON.stringify({ languages }),
    }),

  /** Presenter: push the current position. */
  postPosition: (sessionId: string, slideId: string, buildStep: number): Promise<LivePosition> =>
    authed<LivePosition>(`/api/live/${encodeURIComponent(sessionId)}/position`, {
      method: 'POST',
      body: JSON.stringify({ slideId, buildStep }),
    }),

  /** Presenter: change lifecycle state. */
  setState: (sessionId: string, status: 'live' | 'paused' | 'ended'): Promise<void> =>
    authed<void>(`/api/live/${encodeURIComponent(sessionId)}/state`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    }),

  /** Viewer: fetch the notes-stripped pinned model + current position. */
  getSession: (sessionId: string): Promise<ViewerSession> =>
    anon<ViewerSession>(`/api/live/${encodeURIComponent(sessionId)}`),

  /** Viewer: open the SSE position stream. Caller owns the EventSource lifecycle. */
  openStream: (sessionId: string): EventSource =>
    new EventSource(`${STREAM_BASE}/api/live/${encodeURIComponent(sessionId)}/stream`),

  /** Viewer: fetch (cached-or-generated) per-locale overrides for the session. */
  getTranslations: (sessionId: string, locale: string): Promise<{ locale: string; overrides: DeckOverrides }> =>
    anon(`/api/live/${encodeURIComponent(sessionId)}/translations`, {
      method: 'POST',
      body: JSON.stringify({ locale }),
    }),

  /** Viewer: upsert a presence heartbeat. 204. */
  postPresence: (sessionId: string, viewerId: string, name: string, locale: string): Promise<void> =>
    anon<void>(`/api/live/${encodeURIComponent(sessionId)}/presence`, {
      method: 'POST',
      body: JSON.stringify({ viewerId, name, locale }),
    }),

  /** Viewer: best-effort clean departure via sendBeacon (fires during pagehide,
   *  when a normal fetch would be cancelled). Falls back to a keepalive fetch.
   *  Returns whether a beacon was queued. */
  leaveBeacon: (sessionId: string, viewerId: string): boolean => {
    const url = `/api/live/${encodeURIComponent(sessionId)}/presence/leave`;
    const body = JSON.stringify({ viewerId });
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      return navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    }
    void fetch(url, { method: 'POST', body, headers: { 'Content-Type': 'application/json' }, keepalive: true }).catch(() => {});
    return false;
  },

  /** Viewer: post a question to the shared Q&A. 201 → the created question. */
  postQuestion: (sessionId: string, viewerId: string, name: string, text: string): Promise<Question> =>
    anon<Question>(`/api/live/${encodeURIComponent(sessionId)}/questions`, {
      method: 'POST',
      body: JSON.stringify({ viewerId, name, text }),
    }),

  /** Viewer: upvote a question (idempotent per viewer, server-side). 204. */
  voteQuestion: (sessionId: string, questionId: string, viewerId: string): Promise<void> =>
    anon<void>(`/api/live/${encodeURIComponent(sessionId)}/questions/${encodeURIComponent(questionId)}/vote`, {
      method: 'POST',
      body: JSON.stringify({ viewerId }),
    }),

  /** Presenter: moderate a question (answered | hidden). 204. */
  setQuestionStatus: (sessionId: string, questionId: string, status: 'answered' | 'hidden'): Promise<void> =>
    authed<void>(`/api/live/${encodeURIComponent(sessionId)}/questions/${encodeURIComponent(questionId)}/status`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    }),
};
